import React, { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import AddSharpIcon from '@material-ui/icons/AddSharp';

import { LayerType } from 'yeself-common/typings/guide/LayerCommon';
import { LAYER_TYPE } from 'yeself-common/typings/guide/LayerEnum';
import { GuideSubType } from 'yeself-common/typings/guide/Guide';
import { GUIDE_SUB_TYPE } from 'yeself-common/ModelConstant';
import { FEATURE_FLAG_NAMES } from 'yeself-common/typings/FeatureFlags';

import Feature from 'yeself-client-common/featureFlags/components/Feature';

type CreateGuideButtonProps = {
    onClickMenuItem: (layerType: LayerType, subType: GuideSubType) => void;
};

const CreateGuideButton = ({ onClickMenuItem }: CreateGuideButtonProps): React.ReactElement => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleOnClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleOnClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleOnMenuClick = useCallback(
        (layerType: LayerType, subType: GuideSubType = null) => () => {
            onClickMenuItem(layerType, subType);
            setAnchorEl(null);
        },
        [onClickMenuItem]
    );

    return (
        <>
            <Button
                id="yeself-button"
                variant="contained"
                color="primary"
                aria-owns={anchorEl ? 'long-menu' : ''}
                aria-haspopup="true"
                onClick={handleOnClick}
            >
                <AddSharpIcon fontSize='small' />
                <FormattedMessage id="Buttons.createGuide" defaultMessage="Create guide" />
            </Button>
            <Menu
                id="create-guide-menu"
                open={Boolean(anchorEl)}
                onClose={handleOnClose}
                anchorEl={anchorEl}
                getContentAnchorEl={null}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Feature name={FEATURE_FLAG_NAMES.TOUR}>
                    <Tooltip
                        title={
                            <FormattedMessage
                                id="Guides.menuItem.tour.tooltip"
                                defaultMessage="Tour is a series of sequential steps leading the user through important points of the space."
                            />
                        }
                        placement="left"
                        disableFocusListener
                    >
                        <MenuItem id="yeself-menu-item" onClick={handleOnMenuClick(LAYER_TYPE.TOUR)}>
                            <FormattedMessage id="Guides.menuItem.tour" defaultMessage="Tour" />
                        </MenuItem>
                    </Tooltip>
                </Feature>

                <Feature name={FEATURE_FLAG_NAMES.COLLECTORS}>
                    <Tooltip
                        title={
                            <FormattedMessage
                                id="Guides.menuItem.interaction.collector.tooltip"
                                defaultMessage="It registers spots of interest in your space and it observes user interaction with them in order to trigger future adoption."
                            />
                        }
                        placement="left"
                    >
                        <MenuItem
                            id="yeself-menu-item"
                            onClick={handleOnMenuClick(LAYER_TYPE.INTERACTION_COLLECTOR)}
                        >
                            <FormattedMessage
                                id="Guides.menuItem.interaction.collector"
                                defaultMessage="Interaction collector"
                            />
                        </MenuItem>
                    </Tooltip>

                    <Tooltip
                        title={
                            <FormattedMessage
                                id="Guides.menuItem.error.collector.tooltip"
                                defaultMessage="It observes frontend space error and validation messages and it reports it back to the space owner in order to take actions to decrease error rate production."
                            />
                        }
                        placement="left"
                    >
                        <MenuItem
                            id="yeself-menu-item"
                            onClick={handleOnMenuClick(LAYER_TYPE.ERROR_COLLECTOR)}
                        >
                            <FormattedMessage
                                id="Guides.menuItem.error.collector"
                                defaultMessage="Error Collector"
                            />
                        </MenuItem>
                    </Tooltip>
                </Feature>

                <Feature name={FEATURE_FLAG_NAMES.TOOLTIPS}>
                    <Tooltip
                        title={
                            <FormattedMessage
                                id="Guides.menuItem.application.augmentation.tooltip"
                                defaultMessage="Extends space user interface with additional elements like tooltips, pop-overs or content labels without the need to contact your IT and development."
                            />
                        }
                        placement="left"
                    >
                        <MenuItem
                            id="yeself-menu-item"
                            onClick={handleOnMenuClick(LAYER_TYPE.APPLICATION_AUGMENTATION)}
                        >
                            <FormattedMessage
                                id="Guides.menuItem.application.augmentation"
                                defaultMessage="Tooltips"
                            />
                        </MenuItem>
                    </Tooltip>
                </Feature>

                <Feature name={FEATURE_FLAG_NAMES.PLAYLISTS}>
                    <Tooltip
                        title={
                            <FormattedMessage
                                id="Guides.menuItem.playlist.tooltip"
                                defaultMessage="Embed guidance & support materials such as PDF & videos within Playlists, that can be accessed by users via the Context Help panel."
                            />
                        }
                        placement="left"
                    >
                        <MenuItem id="yeself-menu-item" onClick={handleOnMenuClick(LAYER_TYPE.TOUR, GUIDE_SUB_TYPE.PLAYLIST)}>
                            <FormattedMessage id="Guides.menuItem.playlist" defaultMessage="Playlist" />
                        </MenuItem>
                    </Tooltip>
                </Feature>
            </Menu>
        </>
    );
};

export default CreateGuideButton;
